import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Modal,
  Alert,
  TextField,
  Button,
  Link,
  Snackbar,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginForm from 'components/organisms/loginForm';
import SignupForm from 'components/organisms/signupForm';
import { useAuth } from 'hooks/useAuth';
import {
  validateAccount,
  sendVerificationCode,
  validateVerificationCode,
  checkVerificationStatus
} from 'api';
import EmailIcon from '@mui/icons-material/Email';
import VerificationAlert from './verificationAlert';
import AuthContainer from './authContainer'; // Import the shared container

const Login = ({
  isModal = false,
  onLoginSuccess,
  onClose,
  postAction,
  defaultView = 'login'
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { doLogin } = useAuth();

  const [isLoginPage, setIsLoginPage] = useState(defaultView === 'login');
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState('');
  const [verificationData, setVerificationData] = useState({
    email: '',
    password: '',
    code: '',
    isNewCodeSent: false
  });
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isPendingVerification, setIsPendingVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    // Only run this effect if not in modal mode
    if (!isModal) {
      const params = new URLSearchParams(location.search);
      const id = params.get('id');

      if (id) {
        validateAccount({ id })
          .then((r) => {
            setIsLoginPage(true);
            setDefaultEmail(r.email);
            setVerificationModalOpen(true);
          })
          .catch((err) => {
            console.error('Account validation failed', err);
            navigate('/login');
          });
      }
    }
  }, [location?.search, navigate, isModal]);

  useEffect(() => {
    if (!isModal) {
      setIsLoginPage(location.pathname !== '/signup');
    }
  }, [location?.pathname, isModal]);

  // Rest of your existing functions...
  const handleSignupSuccess = (result) => {
    sendVerificationCode({ email: result.user.email });
    setVerificationData({
      email: result.user.email,
      password: result.password,
      code: '',
      isNewCodeSent: false
    });
    setPendingEmail(result.email);
    setVerificationModalOpen(true);
  };

  const handleVerifyCode = async () => {
    try {
      const response = await validateVerificationCode({
        email: verificationData.email,
        code: verificationData.code
      });

      if (response.status === 200) {
        const loginResult = await doLogin({
          email: verificationData.email,
          password: verificationData.password
        });
        setIsPendingVerification(false);

        if (isModal && onLoginSuccess) {
          onLoginSuccess(loginResult);
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Code verification failed', error);
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleResendCode = async () => {
    const emailToUse = pendingEmail || verificationData.email;

    try {
      await sendVerificationCode({ email: emailToUse });
      setSnackbarMessage(`Verification code has been resent to ${emailToUse}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setVerificationModalOpen(true);

      setVerificationData(prev => ({
        ...prev,
        email: emailToUse,
        isNewCodeSent: true
      }));

    } catch (err) {
      console.error('Error resending code:', err);
      setSnackbarMessage('Failed to resend verification code. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const checkIfAccountPendingVerification = async (email, password) => {
    if (!email) {
      return false;
    }

    try {
      const result = await checkVerificationStatus({ email });
      const PENDING_VALIDATION_STATUS = "code_pending_validation";

      if (result?.message === PENDING_VALIDATION_STATUS) {
        setPendingEmail(email);
        setIsPendingVerification(true);
        setVerificationData(prev => ({
          ...prev,
          email,
          password,
          code: '',
          isNewCodeSent: false
        }));
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking verification status:', error);
      return false;
    }
  };

  const handleLoginSuccess = (result) => {
    if (isModal && onLoginSuccess) {
      onLoginSuccess(result);
    } else {
      navigate('/');
    }
  };

  const handleLoginFailure = async (email, password, errorMessage) => {
    // First check if this is a pending verification issue
    const isPending = await checkIfAccountPendingVerification(email, password);
    
    if (!isPending) {
      // If not pending verification, show the login error
      setLoginError(errorMessage || 'Invalid email or password');
      setSnackbarMessage(errorMessage || 'Invalid email or password');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleVerifyNow = () => {
    setVerificationData(prev => ({
      ...prev,
      email: pendingEmail,
      isNewCodeSent: false
    }));
    setVerificationModalOpen(true);
  };

  const handleVerificationModalClose = () => {
    setVerificationModalOpen(false);
    setIsPendingVerification(true);
    setError('');
  };

  const toggleAuthMode = () => {
    setIsLoginPage(!isLoginPage);
    // Clear any previous errors when switching modes
    setLoginError('');
  };

  const renderContent = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          color="primary"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: isModal ? '1.75rem' : '2.125rem'
          }}
        >
          {isLoginPage ? t('login.title') : t('signup.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isLoginPage ? t('login.subtitle') : t('signup.subtitle')}
        </Typography>
      </Box>

      {isPendingVerification && (
        <VerificationAlert
          email={pendingEmail}
          onVerifyClick={handleVerifyNow}
          onResendCode={handleResendCode}
          isPendingVerification={isPendingVerification}
        />
      )}

      {isLoginPage ? (
        <LoginForm
          defaultEmail={defaultEmail}
          onError={handleLoginFailure}
          onSuccess={handleLoginSuccess}
          postAction={postAction}
          setShowLogin={setIsLoginPage}
          loginError={loginError}
        />
      ) : (
        <SignupForm
          onSuccess={handleSignupSuccess}
          postAction={postAction}
          setShowLogin={setIsLoginPage}
        />          
      )}
    </Box>
  );

  // Modal for verification
  const renderVerificationModal = () => (
    <Modal
      open={verificationModalOpen}
      onClose={() => handleVerificationModalClose()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 450,
          p: 4,
          borderRadius: 2,
          boxShadow: 3,
          mx: 2 // Add margin on mobile
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <EmailIcon sx={{ fontSize: 50, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            We&apos;ve sent {verificationData.isNewCodeSent ? (
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                a new
              </Box>
            ) : 'a'} verification code to <strong>{verificationData.email}</strong>.
            Please check your inbox and enter the code below to complete your registration.
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Verification Code"
          variant="outlined"
          placeholder="Enter 6-digit code"
          value={verificationData.code}
          onChange={(e) => setVerificationData(prev => ({
            ...prev,
            code: e.target.value
          }))}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => handleVerifyCode()}
          size="large"
          sx={{ mb: 2, py: 1.2 }}
        >
          Verify & Continue
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Didn&apos;t receive the code?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={handleResendCode}
              sx={{ fontWeight: 500 }}
            >
              Resend code
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Modal>
  );

  return (
    <>
      <AuthContainer isModal={isModal}>
        {renderContent()}
      </AuthContainer>
      {renderVerificationModal()}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
